const ADMIN_V2_FOUNDATIONS_FLAG_ENV_VAR =
    "VITE_FEATURE_ADMIN_V2_FOUNDATIONS_ENABLED";
const ADMIN_E2E_BYPASS_AUTH_ENV_VAR = "VITE_E2E_BYPASS_AUTH";
const E2E_PERMISSION_KEYS_STORAGE_KEY = "HC_ADMIN_E2E_PERMISSION_KEYS";
const E2E_MENU_VISIBILITY_STORAGE_KEY = "HC_ADMIN_E2E_MENU_VISIBILITY";

const normalizeEnvValue = (value: string | undefined): string | undefined => {
    if (typeof value !== "string") {
        return undefined;
    }

    const normalized = value.trim();
    return normalized.length > 0 ? normalized : undefined;
};

const isTruthyEnvValue = (value: string | undefined): boolean => {
    const normalized = normalizeEnvValue(value)?.toLowerCase();
    if (!normalized) {
        return false;
    }

    return normalized === "true" || normalized === "1";
};

const isLocalHostname = (hostname: string): boolean => {
    return (
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname === "0.0.0.0" ||
        hostname.endsWith(".local")
    );
};

/**
 * Admin Portal v2 foundations feature flag.
 *
 * @remarks
 * Defaults to enabled on localhost for developer iteration, and disabled
 * elsewhere (including preview/production) unless explicitly enabled.
 */
export const isAdminV2FoundationsEnabled = (args?: {
    env?: ImportMetaEnv;
    hostname?: string;
}): boolean => {
    const env = args?.env ?? import.meta.env;
    const raw = normalizeEnvValue(env[ADMIN_V2_FOUNDATIONS_FLAG_ENV_VAR]);

    if (raw !== undefined) {
        return isTruthyEnvValue(raw);
    }

    const hostname =
        args?.hostname ??
        (typeof window === "undefined" ? "" : window.location.hostname);

    return isLocalHostname(hostname);
};

export const isAdminE2eBypassEnabled = (args?: {
    env?: ImportMetaEnv;
}): boolean => {
    const env = args?.env ?? import.meta.env;
    return isTruthyEnvValue(normalizeEnvValue(env[ADMIN_E2E_BYPASS_AUTH_ENV_VAR]));
};

const readJsonFromStorage = <T>(key: string): T | null => {
    if (typeof window === "undefined") {
        return null;
    }

    const raw = window.localStorage.getItem(key);
    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
};

export const getE2eBypassPermissionKeys = (): string[] | null => {
    const parsed = readJsonFromStorage<unknown>(E2E_PERMISSION_KEYS_STORAGE_KEY);
    if (!Array.isArray(parsed)) {
        return null;
    }

    const keys = parsed.filter(
        value => typeof value === "string" && value.trim().length > 0,
    ) as string[];

    return keys.length > 0 ? keys : null;
};

export const getE2eBypassMenuVisibility = (): {
    revenue: boolean;
    learning: boolean;
    sales: boolean;
} | null => {
    const parsed = readJsonFromStorage<unknown>(E2E_MENU_VISIBILITY_STORAGE_KEY);
    if (!parsed || typeof parsed !== "object") {
        return null;
    }

    const value = parsed as {
        revenue?: unknown;
        learning?: unknown;
        sales?: unknown;
    };

    return {
        revenue: typeof value.revenue === "boolean" ? value.revenue : true,
        learning: typeof value.learning === "boolean" ? value.learning : true,
        sales: typeof value.sales === "boolean" ? value.sales : true,
    };
};
